/**
 * StudyDetail — a study's negotiations, tags and recruitment difficulty
 * (plan §11 tab 2: "studies and their negotiations, tags ..., recruitment
 * difficulty, turnaround dates, status"). A negotiation row opens the
 * negotiation page, where the date controls, status and the Versions and
 * Calculator panels live.
 *
 * WP-103, decision (290), 2026-09-29: this is the body of the former
 * StudyDetailModal, laid out on a page. Rendered by StudyPage only.
 */
import { useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { EmptyState, FilterSelect, fieldCls, primaryBtnCls, secondaryBtnCls, useToast } from '../../../ui';
import { listNegotiations, createNegotiation, updateStudy } from '../../api/client';
import type { Negotiation, NegotiationKind, Study } from '../../api/client';
import { NegotiationStatusPill } from './pills';

const KIND_OPTIONS: Array<{ value: NegotiationKind; label: string }> = [
  { value: 'budget', label: 'Budget' },
  { value: 'contract', label: 'Contract' },
  { value: 'amendment_budget', label: 'Budget amendment' },
  { value: 'amendment_contract', label: 'Contract amendment' },
];

const RECRUITMENT_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: '1', label: '1 — easiest' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5 — hardest' },
];

const CARD = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';

interface Props {
  study: Study;
  onStudyUpdated: (study: Study) => void;
}

export function StudyDetail({ study, onStudyUpdated }: Props) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [negotiations, setNegotiations] = useState<Negotiation[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showNewNegotiation, setShowNewNegotiation] = useState(false);
  const [newKind, setNewKind] = useState<NegotiationKind>('budget');
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listNegotiations(study.id)
      .then((rows) => {
        if (!cancelled) setNegotiations(rows);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load negotiations for this study.');
      });
    return () => {
      cancelled = true;
    };
  }, [study.id]);

  async function handleRecruitmentChange(value: string) {
    try {
      const updated = await updateStudy(study.id, { recruitmentDifficulty: value ? Number(value) : null });
      onStudyUpdated(updated);
    } catch {
      toast('Could not update recruitment difficulty.', { type: 'error' });
    }
  }

  async function handleCreateNegotiation(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    try {
      const negotiation = await createNegotiation(study.id, {
        kind: newKind,
        ...(newLabel.trim() ? { label: newLabel.trim() } : {}),
      });
      setNegotiations((prev) => [...(prev ?? []), negotiation]);
      setShowNewNegotiation(false);
      setNewLabel('');
      toast('Negotiation created.', { type: 'success' });
    } catch {
      toast('Could not create the negotiation.', { type: 'error' });
    } finally {
      setCreating(false);
    }
  }

  const tags = [study.tags.sponsorName, study.tags.croName, study.tags.therapeuticAreaName, study.tags.indicationName].filter(
    Boolean
  );

  return (
    <div className="space-y-4">
      <section className={CARD}>
        <div>
          {study.title && <p className="text-sm text-gray-700 dark:text-gray-200">{study.title}</p>}
          {tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Recruitment difficulty</span>
            <FilterSelect
              label="Recruitment difficulty"
              value={study.recruitment_difficulty ? String(study.recruitment_difficulty) : ''}
              onChange={handleRecruitmentChange}
              options={RECRUITMENT_OPTIONS}
            />
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Status</span>
            <p className="pt-1.5 text-sm text-gray-900 dark:text-gray-100">{study.status}</p>
          </div>
        </div>
      </section>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
            Negotiations
          </h3>
          <button type="button" onClick={() => setShowNewNegotiation((v) => !v)} className={secondaryBtnCls}>
            <Plus className="h-4 w-4" />
            New negotiation
          </button>
        </div>

        {showNewNegotiation && (
          <form onSubmit={handleCreateNegotiation} className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Kind</span>
              <FilterSelect label="Kind" value={newKind} onChange={(v) => setNewKind(v as NegotiationKind)} options={KIND_OPTIONS} />
            </div>
            <div className="min-w-[10rem] flex-1">
              <label htmlFor="cta-negotiation-label" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                Label (optional)
              </label>
              <input
                id="cta-negotiation-label"
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                className={`${fieldCls} w-full`}
              />
            </div>
            <button type="submit" disabled={creating} className={primaryBtnCls}>
              {creating ? 'Creating…' : 'Create'}
            </button>
          </form>
        )}

        {loadError && <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}

        {negotiations === null && !loadError && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

        {negotiations !== null && negotiations.length === 0 && (
          <EmptyState title="No negotiations yet" hint="Create one to start tracking dates and status." variant="card" />
        )}

        {negotiations !== null && negotiations.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Kind
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Label
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {negotiations.map((n) => (
                  <tr
                    key={n.id}
                    data-testid={`negotiation-row-${n.id}`}
                    onClick={() => navigate(`/contracts/studies/${study.id}/negotiations/${n.id}`)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`/contracts/studies/${study.id}/negotiations/${n.id}`);
                    }}
                    className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">{n.kind}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{n.label ?? '—'}</td>
                    <td className="px-4 py-3">
                      <NegotiationStatusPill status={n.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudyDetail;
